import React from 'react'
import {
  Box,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Typography,
  Paper,
  Divider,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LeadStatus, LeadSource, GSTVerificationStatus, LeadType } from '../../types/lead'

interface LeadFiltersProps {
  filters: {
    status?: LeadStatus[]
    source?: LeadSource[]
    lead_type?: LeadType | string
    region?: string
    gst_verification_status?: GSTVerificationStatus[]
    location?: string
    city?: string
    state?: string
    interest_category?: string
    campaign_tag?: string
    date_from?: Date | null
    date_to?: Date | null
    search?: string
  }
  onFilterChange: (filters: any) => void
  onClear: () => void
  onSaveSegment?: () => void
}

const statusOptions = [
  { value: LeadStatus.NEW, label: 'New' },
  { value: LeadStatus.CONTACTED, label: 'Contacted' },
  { value: LeadStatus.QUALIFIED, label: 'Qualified' },
  { value: LeadStatus.PROPOSAL_SENT, label: 'Proposal Sent' },
  { value: LeadStatus.NEGOTIATION, label: 'Negotiation' },
  { value: LeadStatus.CONVERTED, label: 'Converted' },
  { value: LeadStatus.LOST, label: 'Lost' },
]

const sourceOptions = [
  { value: LeadSource.INDIAMART, label: 'IndiaMART' },
  { value: LeadSource.FACEBOOK_ADS, label: 'Facebook / Meta Ads' },
  { value: LeadSource.INSTAGRAM, label: 'Instagram' },
  { value: LeadSource.WEBSITE, label: 'Website Inquiry' },
  { value: LeadSource.REFERRAL, label: 'Referral' },
  { value: LeadSource.EMAIL_CAMPAIGN, label: 'Email Campaign' },
  { value: LeadSource.TRADE_SHOW, label: 'Trade Show / Expo' },
  { value: LeadSource.MANUAL_ENTRY, label: 'Manual Entry' },
  { value: LeadSource.OTHER, label: 'Other' },
]

const leadTypeOptions = [
  { value: LeadType.WHOLESALER, label: 'Wholesaler' },
  { value: LeadType.DISTRIBUTOR, label: 'Distributor' },
  { value: LeadType.RETAILER, label: 'Retailer' },
  { value: LeadType.EPC_CONTRACTOR, label: 'EPC Contractor' },
  { value: LeadType.CORPORATE, label: 'Corporate Buyer' },
  { value: LeadType.INDIVIDUAL, label: 'Individual' },
  { value: LeadType.OTHER, label: 'Other' },
]

const regionOptions = [
  { value: 'North', label: 'North India (DL, HR, PB, UP, UK, HP, JK, RJ)' },
  { value: 'West', label: 'West India (MH, GJ, GA)' },
  { value: 'South', label: 'South India (KA, TN, TS, AP, KL)' },
  { value: 'East', label: 'East India (WB, BR, JH, OD)' },
  { value: 'Central', label: 'Central India (MP, CG)' },
  { value: 'North-East', label: 'North-East (AS, ML, TR, MN, NL, MZ, AR, SK)' },
]

const gstStatusOptions = [
  { value: GSTVerificationStatus.VERIFIED, label: 'Verified' },
  { value: GSTVerificationStatus.UNVERIFIED, label: 'Unverified' },
  { value: GSTVerificationStatus.PENDING, label: 'Pending' },
  { value: GSTVerificationStatus.NOT_APPLICABLE, label: 'Not Applicable' },
]

const LeadFilters: React.FC<LeadFiltersProps> = ({
  filters,
  onFilterChange,
  onClear,
  onSaveSegment,
}) => {
  const handleChange = (field: string, value: any) => {
    onFilterChange({ ...filters, [field]: value })
  }

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Filters
      </Typography>

      {/* Search Bar */}
      <TextField
        fullWidth
        size="small"
        label="Search"
        placeholder="Name, phone, email, company..."
        value={filters.search || ''}
        onChange={(e) => handleChange('search', e.target.value)}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Status Multi-Select */}
      <Autocomplete
        multiple
        size="small"
        options={statusOptions}
        getOptionLabel={(option) => option.label}
        value={statusOptions.filter((opt) => filters.status?.includes(opt.value))}
        onChange={(_, newValue) => {
          handleChange('status', newValue.map((v) => v.value))
        }}
        renderInput={(params) => (
          <TextField {...params} label="Status" placeholder="Select statuses" />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label}
              size="small"
            />
          ))
        }
        sx={{ mb: 2 }}
      />

      {/* Source Multi-Select */}
      <Autocomplete
        multiple
        size="small"
        options={sourceOptions}
        getOptionLabel={(option) => option.label}
        value={sourceOptions.filter((opt) => filters.source?.includes(opt.value))}
        onChange={(_, newValue) => {
          handleChange('source', newValue.map((v) => v.value))
        }}
        renderInput={(params) => (
          <TextField {...params} label="Source" placeholder="Select sources" />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label}
              size="small"
            />
          ))
        }
        sx={{ mb: 2 }}
      />

      {/* GST Verification Status Multi-Select */}
      <Autocomplete
        multiple
        size="small"
        options={gstStatusOptions}
        getOptionLabel={(option) => option.label}
        value={gstStatusOptions.filter((opt) =>
          filters.gst_verification_status?.includes(opt.value)
        )}
        onChange={(_, newValue) => {
          handleChange('gst_verification_status', newValue.map((v) => v.value))
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="GST Verification"
            placeholder="Select GST status"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label}
              size="small"
            />
          ))
        }
        sx={{ mb: 2 }}
      />

      {/* Buyer / Lead Type Select */}
      <Autocomplete
        size="small"
        options={leadTypeOptions}
        getOptionLabel={(option) => option.label}
        value={leadTypeOptions.find((opt) => opt.value === filters.lead_type) || null}
        onChange={(_, newValue) => {
          handleChange('lead_type', newValue ? newValue.value : undefined)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Lead / Buyer Type"
            placeholder="Wholesaler, Distributor, Retailer..."
          />
        )}
        sx={{ mb: 2 }}
      />

      {/* Region Select */}
      <Autocomplete
        size="small"
        options={regionOptions}
        getOptionLabel={(option) => option.label}
        value={regionOptions.find((opt) => opt.value === filters.region) || null}
        onChange={(_, newValue) => {
          handleChange('region', newValue ? newValue.value : undefined)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Geographic Region"
            placeholder="North, West, South..."
          />
        )}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Location Filters */}
      <TextField
        fullWidth
        size="small"
        label="Location"
        value={filters.location || ''}
        onChange={(e) => handleChange('location', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        size="small"
        label="City"
        value={filters.city || ''}
        onChange={(e) => handleChange('city', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        size="small"
        label="State"
        value={filters.state || ''}
        onChange={(e) => handleChange('state', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        size="small"
        label="Interest Category"
        value={filters.interest_category || ''}
        onChange={(e) => handleChange('interest_category', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        size="small"
        label="Campaign Tag"
        value={filters.campaign_tag || ''}
        onChange={(e) => handleChange('campaign_tag', e.target.value)}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Date Range */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Date From"
          value={filters.date_from || null}
          onChange={(newValue) => handleChange('date_from', newValue)}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: { mb: 2 },
            },
          }}
        />

        <DatePicker
          label="Date To"
          value={filters.date_to || null}
          onChange={(newValue) => handleChange('date_to', newValue)}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: { mb: 2 },
            },
          }}
        />
      </LocalizationProvider>

      <Divider sx={{ my: 2 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {onSaveSegment && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onSaveSegment}
            sx={{ textTransform: 'none' }}
          >
            Save Segment
          </Button>
        )}
        <Button
          fullWidth
          variant="outlined"
          onClick={onClear}
          sx={{ textTransform: 'none' }}
        >
          Clear Filters
        </Button>
      </Box>
    </Paper>
  )
}

export default LeadFilters
